import * as React from 'react';
import { observer, Observer } from 'mobx-react-lite';
import { Layout, Tabs, Button, Dropdown, Menu, Upload, Spin, message, Tooltip, Modal, Popconfirm } from 'antd';
import {
    PlusOutlined,
    PlayCircleOutlined,
    ImportOutlined,
    ExportOutlined,
    ArrowLeftOutlined,
    SaveOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    DownOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import type { FormInstance } from 'antd/lib/form';
import { Utils } from '../../../common/services/Utils';
import { TrainingProjectDashboardStore, TrainingProjectsStore } from '../../stores';
import {
    CreateEditTrainingProjectExampleModal,
    ImportTrainingProjectExamplesModal,
    ImportReportDialog,
    TrainingProjectExamplesList,
    TrainingProjectSessionsList,
    TrainingProjectSettings
} from '..';
import { CreateEditTrainingProjectFormValues } from '../../types';
import LayoutHeader from '../../../../components/LayoutHeader';
import './TrainingProjectDashboard.less';
import authContext from '../../../authorization/AuthContext';
import { hasPermission } from '../../../authorization/components/HasPermission';
import { AppPermissions } from '../../../authorization/Permissions';

const tabs = {
    settings: {
        key: 'settings',
        title: 'Settings'
    },
    sessions: {
        key: 'sessions',
        title: 'Sessions'
    },
    examples: {
        key: 'examples',
        title: 'Examples'
    }
} as const;

type Props = {
    store: TrainingProjectDashboardStore;
    trainingProjectsStore: TrainingProjectsStore;
};

const TrainingProjectDashboard: React.FC<Props> = ({ store, trainingProjectsStore }) => {
    const { isLoading, trainingProject, examples, hasActiveTrainingSession, isStartingTraining } = store;
    const authCtx = React.useContext(authContext);
    const [activeTab, setActiveTab] = React.useState<keyof typeof tabs>(tabs.settings.key);
    const [isSavingSettings, setIsSavingSettings] = React.useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const settingsFormRef = React.useRef<FormInstance<CreateEditTrainingProjectFormValues>>(null);

    // Load ML models when component mounts
    React.useEffect(() => {
        if (!trainingProjectsStore.isLoadingMlModels) {
            trainingProjectsStore.loadMlModels();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle form submission and update training project
    const handleSaveSettings = async (formValues: CreateEditTrainingProjectFormValues) => {
        if (!trainingProject) {
            message.error('No training project selected');
            return;
        }

        setIsSavingSettings(true);
        try {
            const success = await trainingProject.update(formValues);
            if (success) {
                message.success('Training project settings updated successfully');
                // Reset unsaved changes state after successful save
                setHasUnsavedChanges(false);
            }
        } catch (error) {
            console.error('Error updating training project:', error);
            message.error('Failed to update training project settings');
        } finally {
            setIsSavingSettings(false);
        }
    };

    // Handle form values change to track dirty state
    const handleFormValuesChange = React.useCallback(() => {
        if (!hasUnsavedChanges) {
            setHasUnsavedChanges(true);
        }
    }, [hasUnsavedChanges]);

    const handleTabChange = React.useCallback((targetTab: keyof typeof tabs) => {
        setActiveTab(targetTab);
    }, []);

    // Generate tab title with warning icon for unsaved changes
    const getTabTitle = React.useCallback(
        (tabKey: keyof typeof tabs) => {
            const baseTitle = tabs[tabKey].title;

            if (tabKey === tabs.settings.key && hasUnsavedChanges) {
                return (
                    <span>
                        {baseTitle}
                        <Tooltip title="You have unsaved changes in this tab">
                            <ExclamationCircleOutlined
                                style={{
                                    marginLeft: 8,
                                    color: '#faad14',
                                    fontSize: '12px'
                                }}
                            />
                        </Tooltip>
                    </span>
                );
            }

            return baseTitle;
        },
        [hasUnsavedChanges]
    );

    // Trigger form submission from the settings component
    const triggerSaveSettings = React.useCallback(() => {
        if (settingsFormRef.current) {
            settingsFormRef.current.submit();
        }
    }, []);

    // Handle start training with confirmation if there are unsaved changes
    const handleStartTraining = React.useCallback(() => {
        // If there are unsaved changes, show confirmation dialog
        if (hasUnsavedChanges) {
            Modal.confirm({
                title: 'Unsaved Settings Changes',
                icon: <ExclamationCircleOutlined />,
                content:
                    'You have unsaved changes in the settings tab. Training will use the currently saved settings, not your unsaved changes. Do you want to continue?',
                okText: 'Start Training',
                cancelText: 'Cancel',
                onOk: () => {
                    store.startTraining();
                },
                onCancel: () => {
                    // User cancelled, do nothing
                }
            });
        } else {
            // No unsaved changes, start training directly
            store.startTraining();
        }
    }, [hasUnsavedChanges, store]);

    // Handle CSV file import using Upload component
    const handleCsvFileUpload = React.useCallback(
        async (file: File) => {
            await store.importTrainingProjectExamples(file);
            return false; // Prevent default upload behavior
        },
        [store]
    );

    // Handle import from packages
    const handlePackageImport = React.useCallback(() => {
        store.setImportExamplesDialogVisible(true);
    }, [store]);

    const buttons: JSX.Element[] = React.useMemo(() => {
        if (!trainingProject) {
            return [];
        }

        const returnToListButton = (
            <Button key="return-to-list" size="large" icon={<ArrowLeftOutlined />} onClick={() => store.goBackToList()}>
                Return to list
            </Button>
        );

        if (activeTab === tabs.settings.key) {
            return [
                returnToListButton,
                <Button
                    key="export-training-project"
                    size="large"
                    icon={<ExportOutlined />}
                    onClick={() => store.exportTrainingProject()}
                >
                    Export training project
                </Button>,
                <Button
                    key="save-settings"
                    size="large"
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={triggerSaveSettings}
                    loading={isSavingSettings}
                    disabled={isSavingSettings}
                >
                    Save Settings
                </Button>
            ];
        }

        const hasPermissionToTrain = hasPermission(authCtx.permissions, AppPermissions.CanTrainMlTraining);
        if (activeTab === tabs.sessions.key) {
            return [
                returnToListButton,
                <Tooltip
                    className={hasPermissionToTrain ? '' : 'disabled-tooltip'}
                    title={hasPermissionToTrain ? undefined : 'You don’t have permission to start training.'}
                >
                    <Button
                        key="start-training"
                        size="large"
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        className="start-training-button"
                        onClick={handleStartTraining}
                        disabled={
                            !examples.length || hasActiveTrainingSession || isStartingTraining || !hasPermissionToTrain
                        }
                    >
                        Start training
                    </Button>
                </Tooltip>
            ];
        }

        if (activeTab === tabs.examples.key) {
            const importMenu = (
                <Menu>
                    <Menu.Item key="packages" icon={<ImportOutlined />} onClick={handlePackageImport}>
                        Import from packages
                    </Menu.Item>
                    <Menu.Item key="csv" icon={<FileTextOutlined />}>
                        <Upload
                            accept=".csv,.txt"
                            showUploadList={false}
                            beforeUpload={handleCsvFileUpload}
                            disabled={store.isLoadingImportOperations}
                        >
                            <span style={{ display: 'block', width: '100%' }}>Import from CSV</span>
                        </Upload>
                    </Menu.Item>
                </Menu>
            );

            const jsonExamplesCount = examples.filter(example => example.outputType === 'Json').length;

            const exampleButtons = [
                returnToListButton,
                <Dropdown key="import-examples" overlay={importMenu} trigger={['click']}>
                    <Button size="large" icon={<ImportOutlined />}>
                        Import examples <DownOutlined />
                    </Button>
                </Dropdown>,
                <Button
                    key="export-examples"
                    size="large"
                    icon={<ExportOutlined />}
                    onClick={() => store.exportTrainingProjectExamples()}
                    disabled={!examples.length}
                >
                    Export examples
                </Button>
            ];

            exampleButtons.push(
                <Observer key="bulk-delete">
                    {() => (
                        <>
                            {store.selectedExampleIds.length > 0 && (
                                <Popconfirm
                                    title={`Are you sure you want to delete ${store.selectedExampleIds.length} selected example(s)?`}
                                    onConfirm={() => store.deleteSelectedExamples()}
                                    okText="Delete"
                                    cancelText="Cancel"
                                    okButtonProps={{ danger: true }}
                                >
                                    <Button
                                        size="large"
                                        danger
                                        icon={<DeleteOutlined />}
                                        loading={store.isDeletingExamples}
                                        disabled={store.isDeletingExamples}
                                    >
                                        Delete ({store.selectedExampleIds.length})
                                    </Button>
                                </Popconfirm>
                            )}
                        </>
                    )}
                </Observer>
            );

            if (jsonExamplesCount > 0) {
                exampleButtons.push(
                    <Observer key="validate-all-examples">
                        {() => (
                            <Button
                                size="large"
                                loading={store.isValidatingExamples}
                                onClick={() => store.validateExamples()}
                                disabled={store.isValidatingExamples}
                            >
                                {store.isValidatingExamples
                                    ? 'Validating...'
                                    : `Validate examples (${jsonExamplesCount})`}
                            </Button>
                        )}
                    </Observer>
                );
            }

            exampleButtons.push(
                <Button
                    key="create-example"
                    size="large"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => store.setCreateEditExampleDialogVisible(true)}
                >
                    Create example
                </Button>
            );

            return exampleButtons;
        }

        return [];
    }, [
        activeTab,
        trainingProject,
        examples,
        hasActiveTrainingSession,
        isStartingTraining,
        isSavingSettings,
        store,
        triggerSaveSettings,
        handleStartTraining,
        handlePackageImport,
        handleCsvFileUpload
    ]);

    React.useEffect(() => {
        if (store.project) {
            store.loadAllProjectPackages();
        }
    }, [store, store.project]);

    // Handle editable state based on active tab
    React.useEffect(() => {
        if (activeTab === tabs.settings.key) {
            // Set training project as editable when on settings tab
            if (trainingProject && trainingProjectsStore.editableTrainingProject !== trainingProject) {
                // Clear any existing editable project first
                if (trainingProjectsStore.editableTrainingProject) {
                    trainingProjectsStore.editableTrainingProject.setEditable(false);
                }
                // Set current project as editable
                trainingProject.setEditable(true);
            }
        } else {
            // Clean up editable state when switching away from settings tab
            if (trainingProjectsStore.editableTrainingProject) {
                trainingProjectsStore.editableTrainingProject.setEditable(false);
            }
        }
    }, [activeTab, trainingProject, trainingProjectsStore.editableTrainingProject]);

    // Clean up editable state when component unmounts (navigating away from dashboard)
    React.useEffect(() => {
        return () => {
            if (trainingProjectsStore.editableTrainingProject) {
                trainingProjectsStore.editableTrainingProject.setEditable(false);
            }
        };
    }, [trainingProjectsStore]);

    if (isLoading) {
        return (
            <div className="training-project-dashboard-loading">
                <Spin />
            </div>
        );
    }

    if (!trainingProject) {
        return <div className="training-project-dashboard-not-found">Training project not found</div>;
    }

    return (
        <Layout className="training-project-dashboard">
            <LayoutHeader title={trainingProject.name} subtitle={Utils.getSubtitle(store.project)} buttons={buttons} />
            <CreateEditTrainingProjectExampleModal store={store} />
            <ImportTrainingProjectExamplesModal store={store} />
            <ImportReportDialog
                importResult={store.latestImportResult}
                visible={store.importReportDialogVisible}
                onClose={() => store.setImportReportDialogVisible(false)}
            />
            <Layout.Content>
                <Tabs activeKey={activeTab} onChange={key => handleTabChange(key as keyof typeof tabs)}>
                    <Tabs.TabPane tab={getTabTitle(tabs.settings.key)} key={tabs.settings.key}>
                        <TrainingProjectSettings
                            store={store}
                            trainingProjectsStore={trainingProjectsStore}
                            formRef={settingsFormRef}
                            onSubmit={handleSaveSettings}
                            onFormValuesChange={handleFormValuesChange}
                        />
                    </Tabs.TabPane>
                    <Tabs.TabPane tab={getTabTitle(tabs.sessions.key)} key={tabs.sessions.key}>
                        <TrainingProjectSessionsList store={store} />
                    </Tabs.TabPane>
                    <Tabs.TabPane tab={getTabTitle(tabs.examples.key)} key={tabs.examples.key}>
                        <TrainingProjectExamplesList store={store} />
                    </Tabs.TabPane>
                </Tabs>
            </Layout.Content>
        </Layout>
    );
};

export default observer(TrainingProjectDashboard);
